import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AnimationOption, FontLoaderService } from 'library-explorer';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { SlideContentConfig } from '../models/slide-text-content-config';
import { GlossaryHtmlService } from './glossary-html.service';

@Injectable()
export class SlideContentPipelineService {

  constructor(
    private readonly glossaryHtmlService: GlossaryHtmlService,
    private readonly fontLoaderService: FontLoaderService,
    private readonly sanitizer: DomSanitizer) { }

  public process(config: SlideContentConfig, element: HTMLElement): Observable<SafeHtml> {
    let html = config?.html ?? '';

    this.fontLoaderService.loadHtmlContentFonts(html);

    let processed$ = of(html);

    const wordAnimation = config.wordAnimation;

    if (wordAnimation?.animation) {
      processed$ = processed$.pipe(
        map(content =>
          this.applyAnimationPerWord(content, wordAnimation.animation, wordAnimation.delay)
        )
      );
    }

    if (config.addaptFontSize) {
      processed$ = processed$.pipe(
        map(content => this.addaptFontSize(content))
      );
    }

    if (config.glossaryEnabled) {
      processed$ = processed$.pipe(
        switchMap(content =>
          this.glossaryHtmlService.applyGlossaryTooltipsToHtml(content, element)
        )
      );
    }

    return processed$.pipe(
      map(content => this.sanitizer.bypassSecurityTrustHtml(content)),
      take(1)
    );
  }

  private addaptFontSize(htmlContent: string): string {
    const isPhone = window.matchMedia('(max-width: 640px)').matches;

    if (!isPhone) {
      return htmlContent;
    }

    const regex = /(<[^>]*style="[^"]*font-size\s*:\s*([^"]*)"[^>]*>)/g;

    const modifiedHtml = htmlContent.replace(regex, (_match, element) => {
      return element.replace(/font-size\s*:\s*([^;"]*)/g, 'font-size:clamp(var(--mobile-min-font), $1, var(--mobile-max-font))');
    });

    return modifiedHtml;
  }

  private applyAnimationPerWord(html: string, animation: AnimationOption, delay: number): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const totalAnimationDuration = 2000;

    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);

    const textNodes: Text[] = [];
    const words: { node: Text; parts: string[] }[] = [];

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      if (!node.nodeValue?.trim()) {
        continue;
      }

      const parts = node.nodeValue.split(/(\s+)/);
      words.push({ node, parts });
      textNodes.push(node);
    }

    const wordCount = words.reduce((acc, w) => {
      return acc + w.parts.filter(p => p.trim()).length;
    }, 0);

    const delayStep = totalAnimationDuration / Math.max(wordCount, 1);

    let index = 0;

    for (const { node, parts } of words) {
      const parent = node.parentNode;
      if (!parent) {
        continue;
      }

      const wrapped = parts.map(part => {
        if (!part.trim()) {
          return part;
        }

        const animationDelay = index * delayStep + delay;
        index++;

        return `<span class="animate__animated ${animation}" style="animation-delay: ${animationDelay}ms; display: inline-block;">${part}</span>`;
      }).join('');

      const temp = document.createElement('span');
      temp.innerHTML = wrapped;

      while (temp.firstChild) {
        parent.insertBefore(temp.firstChild, node);
      }

      parent.removeChild(node);
    }

    return doc.body.innerHTML;
  }

}
